import { desglosePieza, formatearMoneda, type Boveda, type ProductoVista } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeftRight, Boxes, PackageMinus, PackagePlus, Pencil, Plus, Search, SlidersHorizontal, Tag, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { AutorizacionDueno } from '@/components/AutorizacionDueno';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Select, Switch } from '@/components/ui/form';
import { Dialogo, PanelLateral } from '@/components/ui/overlay';
import { fechaHora } from '@/lib/utils';
import { aMargenApi, CamposPrecio, desdeProducto, type ValorPrecio } from './CamposPrecio';
import { DialogoEliminarProducto } from './DialogoEliminarProducto';
import { SelectorBoveda } from '../boveda/SelectorBoveda';
import { avisarError } from '@/lib/avisos';

interface Proveedor {
  id: string;
  nombre: string;
}

const NIVEL = { ok: { tono: 'exito', texto: 'Con existencia' }, bajo: { tono: 'aviso', texto: 'Pocas piezas' }, agotado: { tono: 'peligro', texto: 'Agotado' } } as const;
const cent = (t: string) => Math.round((Number.parseFloat(t) || 0) * 100);

export const useProveedores = () => useQuery({ queryKey: ['proveedores'], queryFn: () => api<Proveedor[]>('/proveedores') });

function useInvalidar() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['productos'] });
}

/** Alta / edición de datos generales del producto. */
export function DialogoProducto({
  producto,
  onCerrar,
  onCreado,
}: {
  producto?: ProductoVista;
  onCerrar: () => void;
  /** Con un paquete, también el costo por paquete capturado (para la primera compra, sin redondeos). */
  onCreado?: (p: ProductoVista, costoPaquete?: number) => void;
}) {
  const proveedores = useProveedores();
  const invalidar = useInvalidar();
  const [d, setD] = useState({
    sku: producto?.sku ?? '',
    nombre: producto?.nombre ?? '',
    proveedorId: producto?.proveedor?.id ?? '',
    stockMinimo: String(producto?.stockMinimo ?? 2),
    vendible: producto?.vendible ?? true,
    comisionTipo: producto?.comisionTipo ?? 'porcentaje',
    comision: producto ? String(producto.comisionValor / 100) : '0',
    activo: producto?.activo ?? true,
    tipo: producto?.piezasPorPaquete ? 'paquete' : 'individual',
    piezas: producto?.piezasPorPaquete ? String(producto.piezasPorPaquete) : '',
    costoPaquete: '',
  });
  const [precioCapturado, setPrecio] = useState<ValorPrecio>(producto ? desdeProducto(producto) : { costo: '', margenTipo: 'porcentaje', margen: '40' });
  const [pedirDueno, setPedirDueno] = useState(false);
  const piezas = d.tipo === 'paquete' ? Number.parseInt(d.piezas, 10) || 0 : 0;
  // En el alta de un paquete se captura el costo del paquete; el costo por pieza se calcula.
  const altaPaquete = !producto && d.tipo === 'paquete';
  const costoPieza = piezas >= 2 ? Math.round(cent(d.costoPaquete) / piezas) : 0;
  const precio = altaPaquete ? { ...precioCapturado, costo: d.costoPaquete ? String(costoPieza / 100) : '' } : precioCapturado;
  const pieza = desglosePieza(cent(precio.costo), aMargenApi(precio), { tipo: d.comisionTipo, valor: cent(d.comision) });
  const paqueteIncompleto = d.tipo === 'paquete' && piezas < 2;

  const guardar = useMutation({
    mutationFn: (auth?: { autorizacionToken?: string; solicitarAlDueno?: boolean }) => {
      const comunes = {
        nombre: d.nombre,
        proveedorId: d.proveedorId || null,
        stockMinimo: Number(d.stockMinimo) || 0,
        vendible: d.vendible,
        comisionTipo: d.comisionTipo,
        comisionValor: cent(d.comision),
        piezasPorPaquete: d.tipo === 'paquete' ? piezas : null,
      };
      return producto
        ? api<ProductoVista & { aprobacionPendiente?: string }>(`/productos/${producto.id}`, { method: 'PATCH', body: { ...comunes, sku: d.sku, activo: d.activo, margen: aMargenApi(precio), ...auth } })
        : api<ProductoVista>('/productos', { method: 'POST', body: { ...comunes, sku: d.sku, costo: cent(precio.costo), margen: aMargenApi(precio) } });
    },
    onSuccess: (p: ProductoVista & { aprobacionPendiente?: string }) => {
      if (p.aprobacionPendiente) toast.success('Producto actualizado. El cambio de ganancia se aplicará cuando el dueño lo apruebe.');
      else toast.success(producto ? 'Producto actualizado' : 'Producto registrado. Registra ahora la primera compra.');
      invalidar();
      onCreado?.(p, altaPaquete ? cent(d.costoPaquete) : undefined);
      onCerrar();
    },
    onError: (e) => {
      if (e instanceof ErrorApi && e.code === 'REQUIERE_DUENO') setPedirDueno(true);
      else avisarError(e);
    },
  });

  return (
    <Dialogo
      abierto
      ancho="lg"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={producto ? `Editar ${producto.nombre}` : 'Nuevo producto'}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate(undefined)} cargando={guardar.isPending} disabled={!d.nombre || !d.sku || (!producto && !precio.costo) || paqueteIncompleto}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo etiqueta="SKU" requerido ayuda="Código único del producto">
          {(p) => <Input {...p} value={d.sku} onChange={(e) => setD({ ...d, sku: e.target.value.toUpperCase() })} />}
        </Campo>
        <Campo etiqueta="Nombre" requerido className="sm:col-span-2">
          {(p) => <Input {...p} value={d.nombre} onChange={(e) => setD({ ...d, nombre: e.target.value })} />}
        </Campo>
        <Campo etiqueta="Proveedor" className="sm:col-span-2">
          {(p) => (
            <Select {...p} value={d.proveedorId} onChange={(e) => setD({ ...d, proveedorId: e.target.value })}>
              <option value="">Sin proveedor</option>
              {proveedores.data?.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.nombre}
                </option>
              ))}
            </Select>
          )}
        </Campo>
        <Campo etiqueta="Existencia mínima" ayuda="Avisa al llegar a este número">
          {(p) => <Input {...p} type="number" min="0" value={d.stockMinimo} onChange={(e) => setD({ ...d, stockMinimo: e.target.value })} className="cifras" />}
        </Campo>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Campo etiqueta="¿Individual o paquete?" ayuda="El inventario y la venta siempre van por pieza">
          {(p) => (
            <Select {...p} value={d.tipo} onChange={(e) => setD({ ...d, tipo: e.target.value })}>
              <option value="individual">Individual</option>
              <option value="paquete">Paquete</option>
            </Select>
          )}
        </Campo>
        {d.tipo === 'paquete' && (
          <Campo etiqueta="Piezas por paquete" requerido>
            {(p) => <Input {...p} type="number" min="2" step="1" value={d.piezas} onChange={(e) => setD({ ...d, piezas: e.target.value })} className="cifras" />}
          </Campo>
        )}
        {altaPaquete && (
          <Campo etiqueta="Costo por paquete" requerido ayuda={piezas >= 2 && d.costoPaquete ? `= ${formatearMoneda(costoPieza)} por pieza` : undefined}>
            {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={d.costoPaquete} onChange={(e) => setD({ ...d, costoPaquete: e.target.value })} className="cifras" />}
          </Campo>
        )}
      </div>
      <div className="mt-4">
        <CamposPrecio
          valor={precio}
          onCambiar={setPrecio}
          bloquearCosto={!!producto || altaPaquete}
          etiquetaCosto={altaPaquete ? 'Costo por pieza (calculado)' : undefined}
        />
        {producto && <p className="mt-1 text-[12px] text-foreground-muted">El costo se cambia con «Cambiar costo» o al resurtir. Cambiar la ganancia requiere al dueño.</p>}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Campo etiqueta="Comisión del vendedor">
          {(p) => (
            <Select {...p} value={d.comisionTipo} onChange={(e) => setD({ ...d, comisionTipo: e.target.value as 'porcentaje' | 'fijo' })}>
              <option value="porcentaje">Porcentaje</option>
              <option value="fijo">Monto fijo por pieza</option>
            </Select>
          )}
        </Campo>
        <Campo etiqueta={d.comisionTipo === 'porcentaje' ? 'Comisión (%)' : 'Comisión (MXN)'}>
          {(p) => <Input {...p} type="number" min="0" step="0.01" value={d.comision} onChange={(e) => setD({ ...d, comision: e.target.value })} className="cifras" />}
        </Campo>
        <div className="flex flex-col justify-end gap-2 text-[13px]">
          <label className="flex items-center gap-2">
            <Switch checked={d.vendible} onCheckedChange={(v) => setD({ ...d, vendible: v })} /> Se vende al público
          </label>
          {producto && (
            <label className="flex items-center gap-2">
              <Switch checked={d.activo} onCheckedChange={(v) => setD({ ...d, activo: v })} /> Activo
            </label>
          )}
        </div>
      </div>
      <dl aria-label="Por pieza vendida" className="mt-4 grid grid-cols-3 gap-2 rounded-lg border border-border bg-surface-alt p-3">
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-foreground-muted">Precio de venta</dt>
          <dd className="cifras font-display text-[18px] leading-tight">{formatearMoneda(pieza.precio)}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-foreground-muted">Comisión del vendedor</dt>
          <dd data-testid="comision-pieza" className="cifras font-display text-[18px] leading-tight">
            {formatearMoneda(pieza.comision)}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-foreground-muted">Te queda de ganancia</dt>
          <dd data-testid="ganancia-pieza" className={`cifras font-display text-[18px] leading-tight ${pieza.ganancia < 0 ? 'text-destructive' : 'text-success'}`}>
            {formatearMoneda(pieza.ganancia)}
          </dd>
        </div>
        <p className="col-span-3 text-[12px] text-foreground-muted">Por cada pieza vendida: precio de venta − costo − comisión del vendedor.</p>
      </dl>
      {pedirDueno && (
        <AutorizacionDueno
          accion="cambio_margen"
          motivo="Cambiar la ganancia del producto"
          onCerrar={() => setPedirDueno(false)}
          onAutorizado={(token) => {
            setPedirDueno(false);
            guardar.mutate({ autorizacionToken: token });
          }}
          onEnviarAlDueno={() => {
            setPedirDueno(false);
            guardar.mutate({ solicitarAlDueno: true });
          }}
        />
      )}
    </Dialogo>
  );
}

/** Compra / resurtido: cantidad, costo, reparto venta-uso y (opcional) nueva ganancia. */
export function DialogoCompra({
  producto,
  primera,
  costoPaqueteInicial,
  onCerrar,
}: {
  producto: ProductoVista;
  primera?: boolean;
  /** Costo por paquete capturado en el alta. Al resurtir no se prellena: el costo por pieza está redondeado. */
  costoPaqueteInicial?: number;
  onCerrar: () => void;
}) {
  const invalidar = useInvalidar();
  const { puede } = useSesion();
  const [cantidad, setCantidad] = useState('');
  const [aUso, setAUso] = useState('0');
  const [boveda, setBoveda] = useState<Boveda | ''>('');
  const puedePagar = puede('boveda.retirar');
  const [precioCapturado, setPrecio] = useState<ValorPrecio>(desdeProducto(producto));
  const [pedirDueno, setPedirDueno] = useState(false);
  // Producto en paquete: se capturan paquetes y costo por paquete; el total es exacto (paquetes × costo).
  const porPaquete = producto.piezasPorPaquete;
  const [costoPaquete, setCostoPaquete] = useState(costoPaqueteInicial !== undefined ? String(costoPaqueteInicial / 100) : '');
  const paquetes = Number.parseInt(cantidad, 10) || 0;
  const total = porPaquete ? paquetes * porPaquete : Number(cantidad) || 0;
  const costoPieza = porPaquete ? Math.round(cent(costoPaquete) / porPaquete) : cent(precioCapturado.costo);
  const precio = porPaquete ? { ...precioCapturado, costo: String(costoPieza / 100) } : precioCapturado;
  const totalCompra = porPaquete ? paquetes * cent(costoPaquete) : total * costoPieza;
  const uso = Math.min(Number(aUso) || 0, total);
  const cambiaMargen = precio.margenTipo !== producto.margenTipo || cent(precio.margen) !== producto.margenValor;

  const comprar = useMutation({
    mutationFn: (autorizacionToken?: string) =>
      api('/compras', {
        method: 'POST',
        body: {
          boveda,
          proveedorId: producto.proveedor?.id ?? null,
          lineas: [
            {
              productoId: producto.id,
              ...(porPaquete ? { paquetes, costoPaquete: cent(costoPaquete) } : { cantidad: total, costoUnitario: costoPieza }),
              aVenta: total - uso,
              aUso: uso,
              margen: cambiaMargen ? aMargenApi(precio) : undefined,
            },
          ],
          autorizacionToken,
        },
      }),
    onSuccess: () => {
      toast.success('Compra registrada y stock actualizado');
      invalidar();
      onCerrar();
    },
    onError: (e) => {
      if (e instanceof ErrorApi && e.code === 'REQUIERE_DUENO') setPedirDueno(true);
      else avisarError(e);
    },
  });

  return (
    <Dialogo
      abierto
      ancho="lg"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={primera ? `Primera compra de ${producto.nombre}` : `Resurtir ${producto.nombre}`}
      descripcion="La compra se registra como gasto y suma existencias."
      pie={
        <Button onClick={() => comprar.mutate(undefined)} cargando={comprar.isPending} disabled={total <= 0 || (porPaquete ? !costoPaquete : !precio.costo) || !boveda || !puedePagar}>
          Registrar compra
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo etiqueta={porPaquete ? `Paquetes comprados (de ${porPaquete} piezas)` : 'Piezas compradas'} requerido>
          {(p) => <Input {...p} type="number" min="1" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className="cifras" autoFocus />}
        </Campo>
        <Campo etiqueta="Para uso en servicios" ayuda="No se venden (ej. gel para los cortes)">
          {(p) => <Input {...p} type="number" min="0" max={total} value={aUso} onChange={(e) => setAUso(e.target.value)} className="cifras" />}
        </Campo>
        <div className="rounded-md bg-surface-alt px-3 py-2 text-[13px]">
          <p className="text-foreground-muted">Para venta</p>
          <p className="cifras font-display text-[20px]">{Math.max(0, total - uso)}</p>
        </div>
      </div>
      <div className="mt-4">
        {porPaquete && (
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <Campo etiqueta="Costo por paquete" requerido>
              {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={costoPaquete} onChange={(e) => setCostoPaquete(e.target.value)} className="cifras w-40" />}
            </Campo>
            <p className="cifras pb-2 text-[13px] text-foreground-muted">
              {`= ${total} piezas · ${formatearMoneda(costoPieza)} por pieza · total ${formatearMoneda(totalCompra)}`}
            </p>
          </div>
        )}
        {primera ? (
          // Recién registrado: el costo y la ganancia ya se capturaron en el alta, no se vuelven a pedir.
          <p className="cifras rounded-md bg-surface-alt px-3 py-2 text-[13px] text-foreground-muted">
            Costo {formatearMoneda(cent(precio.costo))} por pieza · precio de venta {formatearMoneda(producto.precioVenta)}
          </p>
        ) : (
          <CamposPrecio valor={precio} onCambiar={setPrecio} bloquearCosto={!!porPaquete} etiquetaCosto={porPaquete ? 'Costo por pieza (calculado)' : undefined} />
        )}
        {cambiaMargen && !puede('inventario.margen_cambiar') && <p className="mt-1 text-[12px] text-warning">Cambiar la ganancia pedirá la contraseña del dueño.</p>}
        <div className="mt-4 flex flex-col gap-1">
          <div className="sm:w-72">
            <SelectorBoveda valor={boveda} onCambiar={setBoveda} />
          </div>
          {!puedePagar && <p className="text-[12.5px] text-warning">Para registrar compras necesitas el permiso de retirar de la bóveda</p>}
        </div>
        <p className="cifras mt-3 text-right text-[14px]">
          Total de la compra: <strong>{formatearMoneda(totalCompra)}</strong>
        </p>
      </div>
      {pedirDueno && (
        <AutorizacionDueno
          accion="cambio_margen"
          motivo="Cambiar la ganancia al resurtir"
          onCerrar={() => setPedirDueno(false)}
          onAutorizado={(token) => {
            setPedirDueno(false);
            comprar.mutate(token);
          }}
        />
      )}
    </Dialogo>
  );
}

type AccionStock = 'consumo' | 'transferir' | 'ajuste' | 'costo';

export function DialogoStock({ producto, accion, onCerrar }: { producto: ProductoVista; accion: AccionStock; onCerrar: () => void }) {
  const invalidar = useInvalidar();
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [bolsa, setBolsa] = useState<'venta' | 'uso'>('uso');
  // En un producto de paquete el costo se captura por paquete; la API guarda el de la pieza.
  const porPaquete = producto.piezasPorPaquete;
  const [costo, setCosto] = useState(((producto.costo * (porPaquete ?? 1)) / 100).toString());
  const costoPieza = porPaquete ? Math.round(cent(costo) / porPaquete) : cent(costo);
  const piezaEnCero = Boolean(porPaquete) && cent(costo) > 0 && costoPieza < 1;
  const titulos = { consumo: 'Registrar consumo interno', transferir: 'Pasar entre venta y uso', ajuste: 'Ajuste de inventario', costo: 'Cambiar costo del proveedor' };

  const enviar = useMutation({
    mutationFn: () => {
      const n = Number(cantidad);
      if (accion === 'costo') return api(`/productos/${producto.id}/costo`, { method: 'PATCH', body: { costo: costoPieza } });
      if (accion === 'consumo') return api(`/productos/${producto.id}/consumo`, { method: 'POST', body: { cantidad: n, motivo } });
      if (accion === 'transferir') return api(`/productos/${producto.id}/transferir`, { method: 'POST', body: { de: bolsa, a: bolsa === 'venta' ? 'uso' : 'venta', cantidad: n, motivo } });
      return api(`/productos/${producto.id}/ajuste`, { method: 'POST', body: { bolsa, cantidad: n, motivo } });
    },
    onSuccess: () => {
      toast.success('Inventario actualizado');
      invalidar();
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={titulos[accion]}
      descripcion={producto.nombre}
      pie={
        <Button onClick={() => enviar.mutate()} cargando={enviar.isPending} disabled={accion === 'costo' ? !costo || piezaEnCero : !cantidad || !motivo.trim()}>
          Guardar
        </Button>
      }
    >
      {accion === 'costo' ? (
        <Campo
          etiqueta={porPaquete ? `Nuevo costo por paquete (de ${porPaquete} piezas)` : 'Nuevo costo (MXN)'}
          ayuda={
            piezaEnCero
              ? `El costo por pieza quedaría en ${formatearMoneda(0)}; revisa el costo del paquete.`
              : `${porPaquete && costo ? `= ${formatearMoneda(costoPieza)} por pieza. ` : ''}El precio de venta se recalcula con la ganancia actual.`
          }
        >
          {(p) => <Input {...p} type="number" min="0" step="0.01" value={costo} onChange={(e) => setCosto(e.target.value)} className="cifras" />}
        </Campo>
      ) : (
        <div className="grid gap-3">
          {accion !== 'consumo' && (
            <Campo etiqueta={accion === 'transferir' ? 'Mover desde' : 'Bolsa'}>
              {(p) => (
                <Select {...p} value={bolsa} onChange={(e) => setBolsa(e.target.value as 'venta' | 'uso')}>
                  <option value="venta">Para venta ({producto.stockVenta})</option>
                  <option value="uso">Uso en servicios ({producto.stockUso})</option>
                </Select>
              )}
            </Campo>
          )}
          <Campo etiqueta="Cantidad" ayuda={accion === 'ajuste' ? 'Negativo para restar (merma, pérdida)' : undefined}>
            {(p) => <Input {...p} type="number" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className="cifras" />}
          </Campo>
          <Campo etiqueta="Motivo">{(p) => <Input {...p} value={motivo} onChange={(e) => setMotivo(e.target.value)} />}</Campo>
        </div>
      )}
    </Dialogo>
  );
}

interface Movimiento {
  id: string;
  tipo: string;
  bolsa: 'venta' | 'uso';
  cantidad: number;
  motivo: string | null;
  referencia: string | null;
  usuario: string | null;
  fecha: string;
}

export function PanelProducto({ id, onCerrar }: { id: string; onCerrar: () => void }) {
  const { puede } = useSesion();
  const producto = useQuery({ queryKey: ['productos', id], queryFn: () => api<ProductoVista>(`/productos/${id}`) });
  const movs = useQuery({ queryKey: ['productos', id, 'movimientos'], queryFn: () => api<Movimiento[]>(`/productos/${id}/movimientos`) });
  const [dialogo, setDialogo] = useState<'editar' | 'compra' | 'eliminar' | AccionStock | null>(null);
  const p = producto.data;
  const editable = puede('inventario.editar');
  const eliminable = puede('inventario.eliminar');

  return (
    <PanelLateral abierto onCambiarAbierto={(v) => !v && onCerrar()} titulo={p?.nombre ?? 'Producto'} descripcion={p ? `${p.sku}${p.proveedor ? ` · ${p.proveedor.nombre}` : ''}` : undefined}>
      {!p ? (
        <Esqueleto className="h-60" />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              ['Para venta', p.stockVenta],
              ['Apartados', p.stockReservado],
              ['Uso interno', p.stockUso],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-surface-alt px-2 py-2">
                <p className="cifras font-display text-[22px] leading-none">{v}</p>
                <p className="mt-1 text-[11px] text-foreground-muted">{k}</p>
              </div>
            ))}
          </div>
          <dl className="cifras grid grid-cols-2 gap-y-1.5 text-[13.5px]">
            <dt className="text-foreground-muted">Costo</dt>
            <dd className="text-right">{formatearMoneda(p.costo)}</dd>
            <dt className="text-foreground-muted">Ganancia</dt>
            <dd className="text-right">{p.margenTipo === 'porcentaje' ? `${p.margenValor / 100}%` : formatearMoneda(p.margenValor)}</dd>
            <dt className="text-foreground-muted">Precio de venta</dt>
            <dd className="text-right font-semibold">{formatearMoneda(p.precioVenta)}</dd>
            <dt className="text-foreground-muted">Mínimo</dt>
            <dd className="text-right">{p.stockMinimo}</dd>
          </dl>
          {editable && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => setDialogo('compra')} disabled={!puede('boveda.retirar')}>
                <PackagePlus /> Resurtir
              </Button>
              <Button variante="secundario" onClick={() => setDialogo('editar')}>
                <Pencil /> Editar
              </Button>
              <Button variante="secundario" onClick={() => setDialogo('costo')}>
                <Tag /> Cambiar costo
              </Button>
              <Button variante="secundario" onClick={() => setDialogo('consumo')}>
                <PackageMinus /> Consumo interno
              </Button>
              <Button variante="secundario" onClick={() => setDialogo('transferir')}>
                <ArrowLeftRight /> Venta ↔ uso
              </Button>
              <Button variante="secundario" onClick={() => setDialogo('ajuste')}>
                <SlidersHorizontal /> Ajuste
              </Button>
              {!puede('boveda.retirar') && (
                <p className="col-span-2 text-[12.5px] text-warning">Para registrar compras necesitas el permiso de retirar de la bóveda</p>
              )}
            </div>
          )}
          {eliminable && (
            <div className="border-t border-border pt-4">
              <Button variante="peligro" className="w-full" onClick={() => setDialogo('eliminar')}>
                <Trash2 /> Eliminar producto
              </Button>
            </div>
          )}
          <div>
            <h3 className="mb-2 text-[16px]">Movimientos</h3>
            <ul className="flex flex-col gap-1">
              {movs.data?.map((m) => (
                <li key={m.id} className="flex items-center gap-2 rounded-md bg-surface-alt px-3 py-1.5 text-[12.5px]">
                  <span className={`cifras w-10 text-right font-semibold ${m.cantidad < 0 ? 'text-destructive' : 'text-success'}`}>{m.cantidad > 0 ? `+${m.cantidad}` : m.cantidad}</span>
                  <span className="flex-1">
                    <span className="capitalize">{m.tipo}</span> · {m.bolsa === 'venta' ? 'venta' : 'uso'}
                    {m.motivo ? ` · ${m.motivo}` : ''}
                    <span className="block text-foreground-muted">
                      {fechaHora(m.fecha)}
                      {m.usuario ? ` · ${m.usuario}` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {dialogo === 'editar' && <DialogoProducto producto={p} onCerrar={() => setDialogo(null)} />}
          {dialogo === 'compra' && <DialogoCompra producto={p} onCerrar={() => setDialogo(null)} />}
          {(dialogo === 'consumo' || dialogo === 'transferir' || dialogo === 'ajuste' || dialogo === 'costo') && <DialogoStock producto={p} accion={dialogo} onCerrar={() => setDialogo(null)} />}
          {dialogo === 'eliminar' && (
            <DialogoEliminarProducto
              producto={p}
              onCerrar={() => setDialogo(null)}
              onEliminado={() => {
                setDialogo(null);
                onCerrar();
              }}
            />
          )}
        </div>
      )}
    </PanelLateral>
  );
}

export function InventarioPage() {
  const { id } = useSearch({ strict: false }) as { id?: string };
  const navegar = useNavigate();
  const { puede } = useSesion();
  const [busqueda, setBusqueda] = useState('');
  const [diferida, setDiferida] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [primeraCompra, setPrimeraCompra] = useState<{ producto: ProductoVista; costoPaquete?: number } | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDiferida(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);
  const productos = useQuery({ queryKey: ['productos', 'lista', diferida], queryFn: () => api<ProductoVista[]>('/productos', { query: { q: diferida, inactivos: 'true' } }) });

  return (
    <ModuleLayout titulo="Inventario" secciones={SECCIONES_POR_MODULO.inventario}>
      <EncabezadoPagina
        titulo="Productos"
        descripcion="Existencias para venta y para uso en servicios, costo, ganancia y precio."
        acciones={
          puede('inventario.editar') && (
            <Button onClick={() => setNuevo(true)}>
              <Plus /> Nuevo producto
            </Button>
          )
        }
      />
      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" aria-hidden />
        <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por nombre o SKU…" className="h-10 pl-9" aria-label="Buscar producto" />
      </div>
      <Tarjeta>
        {productos.isLoading ? (
          <Esqueleto className="m-4 h-40" />
        ) : !productos.data?.length ? (
          <Vacio icono={<Boxes />} titulo="Sin productos" descripcion="Registra un producto y su primera compra para tener existencias." />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>SKU</Th>
                <Th>Producto</Th>
                <Th className="text-right">Costo</Th>
                <Th className="text-right">Precio</Th>
                <Th className="text-right">Venta</Th>
                <Th className="text-right">Uso</Th>
                <Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {productos.data.map((p) => (
                <Tr key={p.id} className={`cursor-pointer ${p.activo ? '' : 'opacity-50'}`} onClick={() => void navegar({ to: '/inventario', search: { id: p.id } })}>
                  <Td className="cifras text-foreground-muted">{p.sku}</Td>
                  <Td className="font-medium">
                    {p.nombre}
                    {!p.vendible && <span className="ml-2 text-[11.5px] text-foreground-muted">(solo uso)</span>}
                  </Td>
                  <Td className="cifras text-right">{formatearMoneda(p.costo)}</Td>
                  <Td className="cifras text-right font-medium">{formatearMoneda(p.precioVenta)}</Td>
                  <Td className="cifras text-right">
                    {p.stockVenta}
                    {p.stockReservado ? <span className="text-foreground-muted"> ({p.stockReservado} apart.)</span> : null}
                  </Td>
                  <Td className="cifras text-right">{p.stockUso}</Td>
                  <Td>{p.vendible && <Insignia tono={NIVEL[p.nivel].tono}>{NIVEL[p.nivel].texto}</Insignia>}</Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>
      {id && <PanelProducto id={id} onCerrar={() => void navegar({ to: '/inventario', search: {} })} />}
      {nuevo && <DialogoProducto onCerrar={() => setNuevo(false)} onCreado={(producto, costoPaquete) => setPrimeraCompra({ producto, costoPaquete })} />}
      {primeraCompra && <DialogoCompra producto={primeraCompra.producto} costoPaqueteInicial={primeraCompra.costoPaquete} primera onCerrar={() => setPrimeraCompra(null)} />}
    </ModuleLayout>
  );
}
